import ReportProblemIcon from '@/assets/icons/report_problem.svg'
import { Button } from '@/components/ui/button'
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { buttonVariants } from '@/lib/component-constants'
import { cn } from '@/lib/utils'
import { CircleAlert } from 'lucide-react'
import type { VariantProps } from 'class-variance-authority'
import type { ReactNode } from 'react'

type ConfirmationDialogVariant = 'success' | 'warning' | 'error'

type ConfirmationDialogAction = {
  label: string
  onClick?: () => void
  variant?: VariantProps<typeof buttonVariants>['variant']
  closeOnClick?: boolean
  disabled?: boolean
}

type ConfirmationDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  icon?: ReactNode
  variant: ConfirmationDialogVariant
  title: ReactNode
  description?: ReactNode | string
  children?: ReactNode
  actions: ConfirmationDialogAction[]
  className?: string
}

const variantIconClassName: Record<ConfirmationDialogVariant, string> = {
  success: 'bg-main-support-surface-green text-green-600',
  warning: 'bg-main-support-surface-primary text-primary-700',
  error: 'bg-main-support-surface-red text-red-600',
}

const defaultIcon: Record<ConfirmationDialogVariant, ReactNode> = {
  success: <CircleAlert className="size-10" strokeWidth={2.25} />,
  warning: <ReportProblemIcon className="size-10" />,
  error: <ReportProblemIcon className="size-10" />,
}

export default function ConfirmationDialog({
  open,
  onOpenChange,
  icon,
  variant,
  title,
  description,
  children,
  actions,
  className,
}: ConfirmationDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'w-118 rounded-400 bg-main-surface-lv-02 p-6 shadow-[6px_4px_4px_var(--color-main-elevation-lv03)]',
          className,
        )}
        showCloseButton={false}
      >
        <div className="flex flex-col items-center gap-6">
          <div className={cn('flex items-center justify-center rounded-300 p-2', variantIconClassName[variant])}>
            {icon ?? defaultIcon[variant]}
          </div>
          <div className="flex flex-col items-center gap-2 px-2">
            <DialogTitle className="text-center text-ui-header-h4 leading-ui-header-h4 font-semibold text-main-text-lv01 h-full">
              {title}
            </DialogTitle>
            {description && (
              <DialogDescription className="whitespace-pre-line text-center text-comp-input-ip2 leading-comp-input-ip2 text-main-text-lv02">
                {typeof description === 'string' ? (
                  <span dangerouslySetInnerHTML={{ __html: description }} />
                ) : (
                  description
                )}
              </DialogDescription>
            )}
          </div>
          {children}
          <div className="flex w-full items-center gap-2">
            {actions.map((action) => {
              const button = (
                <Button
                  key={action.label}
                  type="button"
                  variant={action.variant || (variant === 'error' ? 'danger' : 'primary')}
                  size="md"
                  className="flex-1"
                  onClick={action.onClick}
                  disabled={action.disabled}
                >
                  {action.label}
                </Button>
              )

              if (action.closeOnClick === false) return button

              return (
                <DialogClose key={action.label} render={button}>
                  {action.label}
                </DialogClose>
              )
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export type { ConfirmationDialogAction, ConfirmationDialogProps, ConfirmationDialogVariant }
